import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { istApiFehler } from "./api/client";
// NUR diese zwei Stildateien, in dieser Reihenfolge, und nur hier:
// index.css bindet tokens.css VOR base.css ein (base.css benutzt die
// Variablen, die tokens.css setzt), app.css ergänzt danach die Schale.
import "./styles/index.css";
import "./styles/app.css";
import { App } from "./App";

/**
 * Die Vorgaben für JEDE Abfrage — an EINER Stelle, nicht je Haken.
 *
 * `retry` wiederholt nur, was sich durch Wiederholen heilen lässt: ein 4xx
 * bleibt ein 4xx, und drei Versuche darauf sind drei Fehlermeldungen. Ein 401
 * wird ausdrücklich NICHT wiederholt — der Riegel in client.ts hat den
 * Anmeldestatus da längst zurückgesetzt, und der Wächter führt zur Anmeldung.
 *
 * `refetchOnWindowFocus` steht hier auf `false` und wird von `useStand()`
 * einzeln eingeschaltet (SPEC §7): der Stand ist die eine Antwort, an der
 * mehrere Leute gleichzeitig ziehen; ein Projektblatt oder die Nutzerliste
 * müssen nicht bei jedem Fensterwechsel neu geholt werden.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (n: number, e: unknown) => n < 2 && !(istApiFehler(e) && e.status >= 400 && e.status < 500),
    },
    mutations: {
      // Eine Änderung wird NIE von selbst wiederholt: „Projekt anlegen" zweimal
      // ist zwei Projekte. Wer es noch einmal versuchen will, drückt noch
      // einmal.
      retry: false,
    },
  },
});

// Nach einem Deploy tragen die Bausteine neue Namen, und ein Reiter, der die
// alte index.html noch offen hat, holt beim naechsten Bildschirmwechsel einen
// Baustein, den es nicht mehr gibt („Failed to fetch dynamically imported
// module"). Vite meldet das als `vite:preloadError`; die ehrliche Antwort ist
// EINMAL neu laden — der Merker verhindert eine Schleife, falls der Fehler
// eine andere Ursache hat (dann zeigt der Auffangkorb sie).
const NEU_GELADEN = "lp.neu-geladen";
window.addEventListener("vite:preloadError", (ereignis) => {
  let schon = false;
  try {
    schon = sessionStorage.getItem(NEU_GELADEN) === "1";
    if (!schon) sessionStorage.setItem(NEU_GELADEN, "1");
  } catch {
    /* ohne Speicher: einmal versuchen ist immer noch besser als nie */
  }
  if (schon) return;
  ereignis.preventDefault();
  window.location.reload();
});
try {
  // Der Merker gilt fuer EINEN Neuladeversuch; ist die Seite danach heil
  // hochgekommen, darf das naechste Deploy wieder einen ausloesen.
  window.setTimeout(() => sessionStorage.removeItem(NEU_GELADEN), 10_000);
} catch {
  /* siehe oben */
}

const wurzel = document.getElementById("root");
if (!wurzel) throw new Error("Kein #root im Dokument — index.html wurde verändert.");

// Der QueryClientProvider gehört HIERHER und nirgends sonst: Karte, Board,
// Zeitleiste, Matrix, Projektblatt und Verwaltung rufen alle `useQuery`; ohne
// den Provider wirft der ERSTE dieser Aufrufe „No QueryClient set", und die
// Startseite wäre eine weiße Fläche im Auffangkorb.
createRoot(wurzel).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
